/*
 * The keyboard sheet (Ctrl+/ or ?). Most of it is rendered *from* the registry, scope by scope,
 * which is the whole point of having one: a binding that is not in the sheet does not exist, and a
 * module's bindings appear here the moment the module is switched on (A31 K1–K5) — under the
 * headings of the scopes the module declares (`shortcutScopes`), after core's own.
 *
 * Writing a message is the exception, and it is generated rather than registered. The editor's keys
 * are a ProseMirror keymap and the nine formatting chords are one table, `SHORTCUT_CHORDS`, which
 * already feeds the toolbar's tooltips and the source view; reading that table here is what keeps a
 * chord change from having to be typed a second time. Until this section existed the sheet
 * documented ten of the sixteen keys a person can press, and `/Bold/i` matched nothing in it
 * (X-A2 D8, X-A1 A8).
 *
 * A document's keys are the other exception: the Documents section (`core/docs/KeySheet.tsx`) is a
 * lazy chunk with the document's own words, drawn from the text's chord table (`DOC_CHORDS`) and the
 * registry's `document` scope, so the first screen never carries forty rows nobody asked for.
 *
 * One vocabulary for the modifiers. The sheet used to print `Ctrl/Cmd K` beside `Ctrl Shift A` and
 * left the reader to work out whether the difference meant anything; it names the key *this*
 * machine has instead — `Cmd` on a Mac, `Ctrl` everywhere else — so every row reads the same way
 * and no row is a lie about a keyboard (X-A3 P14). Two chords for one binding are alternatives and
 * are joined by the word.
 */

import { lazy, Suspense, useEffect, useRef } from "react"
import { Dialog } from "../../ui/index.ts"
import { useComposerSettings } from "../composer/settings.ts"
import { keyOfCode, SHORTCUT_CHORDS, SHORTCUTS } from "../composer/text/shortcuts.ts"
import { DOC_SCOPES } from "../docs/shortcuts.ts"
import { t, translate, type PlainKey } from "../i18n/index.ts"
import { useSlot } from "../modules/slots.ts"
import "./shell.css"
import { useShortcutRegistry } from "./ShortcutProvider.tsx"
import {
	IS_MAC,
	keyParts,
	learnKeyLabels,
	MOD_KEY,
	printKeys,
	type ShortcutScope,
} from "./shortcuts.ts"

/**
 * The documents' section (`core/docs/KeySheet.tsx`): forty-odd rows in the document's own words,
 * fetched when the sheet opens rather than carried by every first screen. It draws nothing until it
 * has arrived, a moment after the rest of the sheet, and it takes everything of the shell's from
 * here as props, so that it shares no module with the first screen (the file says why).
 */
const DocumentKeys = lazy(() => import("../docs/KeySheet.tsx"))

/** One line of the sheet: what it does, and the chords that do it. */
export interface SheetRow {
	id: string
	/** A key of this build or of a module's namespace; `translate` renders both. */
	labelKey: string
	/** Key specs in the registry's own spelling, so both sources render through one path. */
	chords: string[]
}

const REGISTRY_SCOPES: { scope: ShortcutScope; titleKey: PlainKey }[] = [
	{ scope: "global", titleKey: "shell.shortcuts.scope.global" },
	{ scope: "conversation", titleKey: "shell.shortcuts.scope.conversation" },
	{ scope: "messageList", titleKey: "shell.shortcuts.scope.messageList" },
	{ scope: "composer", titleKey: "shell.shortcuts.scope.composer" },
]

/** The nine formatting chords, straight out of the table the editor and the source view share. */
const FORMATTING: SheetRow[] = SHORTCUTS.map((id) => ({
	id: `format.${id}`,
	labelKey: `composer.format.${id}`,
	chords: [
		`mod+${SHORTCUT_CHORDS[id].shift ? "shift+" : ""}${keyOfCode(SHORTCUT_CHORDS[id].code)}`,
	],
}))

/**
 * The rest of what the message box answers (`core/editor/keymap.ts`), plus the browser's own
 * paste-without-formatting, which is the mirror of holding Shift while pasting.
 *
 * Exported because it is also the list of chords the application must not take for itself: the
 * collision test feeds it to the registry beside the nine formatting chords, which is what would
 * have caught `Ctrl+K` being both the quick switcher and *link* at build time (X-A4 D1).
 */
export const EDITING: SheetRow[] = [
	{ id: "sendAnywhere", labelKey: "shell.shortcut.sendAnywhere", chords: ["mod+enter"] },
	{ id: "editLast", labelKey: "shell.shortcut.editLast", chords: ["arrowup"] },
	{ id: "listIn", labelKey: "shell.shortcut.listIn", chords: ["tab"] },
	{ id: "listOut", labelKey: "shell.shortcut.listOut", chords: ["shift+tab"] },
	{ id: "undo", labelKey: "shell.shortcut.undo", chords: ["mod+z"] },
	{ id: "redo", labelKey: "shell.shortcut.redo", chords: ["mod+y", "mod+shift+z"] },
	{ id: "pastePlain", labelKey: "shell.shortcut.pastePlain", chords: ["mod+shift+v"] },
]

/** One row's chords, a keycap per key and "or" between two chords; the documents' section draws with it. */
export function SheetChords({ chords }: { chords: readonly string[] }) {
	return (
		<span className="tb-sheet__keys">
			{chords.map((spec, index) => (
				<span key={spec} className="tb-sheet__chord">
					{index === 0 ? null : <span className="tb-sheet__or">{t("shell.shortcuts.or")}</span>}
					{keyParts(spec).map((part) => (
						<kbd key={part}>{part}</kbd>
					))}
				</span>
			))}
		</span>
	)
}

/** One heading and its rows; `titleKey` is core's or a module's, which `translate` renders alike. */
function Section({ titleKey, rows }: { titleKey: string; rows: readonly SheetRow[] }) {
	if (rows.length === 0) return null
	return (
		<section>
			<h3 className="tb-sheet__title">{translate(titleKey)}</h3>
			<dl className="tb-sheet__list">
				{rows.map((row) => (
					<div key={row.id} className="tb-sheet__row">
						<dt>{translate(row.labelKey)}</dt>
						<dd>
							<SheetChords chords={row.chords} />
						</dd>
					</div>
				))}
			</dl>
		</section>
	)
}

export function ShortcutSheet({
	open,
	onOpenChange,
}: {
	open: boolean
	onOpenChange: (open: boolean) => void
}) {
	const registry = useShortcutRegistry()
	const moduleScopes = useSlot("shortcutScopes")
	// Which key sends is this person's own setting, so the sheet says what *their* box does rather
	// than what the default box does.
	const { enterSends } = useComposerSettings()
	const region = useRef<HTMLDivElement>(null)

	/*
	 * Where the sheet opens. The dialog's primitive focuses the first tabbable thing it contains, and
	 * in this dialog that is the scrolling list itself — so the sheet came up with a blue hairline
	 * drawn down both edges of a list nobody had asked to focus (X-B1 P11). The focus belongs on the
	 * way out: a reader who opened a reference with `?` wants to read it and then close it, the wheel
	 * scrolls the list either way, and Tab still reaches the region for the arrow keys (which is what
	 * its tab stop is for, and the one case where the ring is right).
	 *
	 * On the next frame, and not in the effect itself: the dialog's content is portalled, so the
	 * primitive's own focus lands in a commit of its own *after* this effect has run (measured — the
	 * list held the focus when the effect focused the button directly). A frame is early enough that
	 * nothing is painted in between and late enough to be last. The close button is the dialog's own,
	 * reached through the dialog this region stands in rather than through the document, because two
	 * dialogs can be mounted at once.
	 */
	useEffect(() => {
		if (!open) return undefined
		// The keyboard's own keycaps for the chords that name a position, where the browser can say.
		learnKeyLabels()
		const frame = requestAnimationFrame(() => {
			const dialog = region.current?.closest('[role="dialog"]')
			dialog?.querySelector<HTMLElement>(".tb-dialog__close")?.focus()
		})
		return () => cancelAnimationFrame(frame)
	}, [open])

	const rowsOf = (scope: ShortcutScope): SheetRow[] =>
		registry.forScope(scope).map((def) => ({
			id: def.id,
			labelKey: def.labelKey,
			chords: [...registry.keysOf(def.id)],
		}))

	const writing: SheetRow[] = [
		{
			id: "send",
			labelKey: "shell.shortcut.send",
			chords: [enterSends ? "enter" : "shift+enter"],
		},
		{
			id: "newline",
			labelKey: "shell.shortcut.newline",
			chords: [enterSends ? "shift+enter" : "enter"],
		},
		...EDITING,
		...FORMATTING,
	]

	return (
		<Dialog open={open} onOpenChange={onOpenChange} title={t("shell.shortcuts.title")}>
			{/* A long read with nothing to operate in it, inside a dialog whose body is the scroller.
			    A scrolling region no keyboard can reach is a region a keyboard reader cannot read, so
			    the list takes a tab stop of its own: Tab lands here and the arrow keys scroll. */}
			{/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- a scrolling region with
			    nothing focusable in it is exactly the case the tab stop exists for (axe
			    `scrollable-region-focusable`). */}
			<div className="tb-sheet" tabIndex={0} ref={region}>
				{REGISTRY_SCOPES.map(({ scope, titleKey }) => (
					<Section key={scope} titleKey={titleKey} rows={rowsOf(scope)} />
				))}
				<Section titleKey="shell.shortcuts.scope.writing" rows={writing} />
				{/* The scopes that are pages of their own — core's Activity, then each module's, in
				    modules.ts order — each drawn only while something has registered a binding for it
				    (`Section` renders nothing for an empty list): a heading over nothing says nothing. */}
				<Section titleKey="shell.shortcuts.scope.activity" rows={rowsOf("activity")} />
				<Suspense fallback={null}>
					<DocumentKeys
						commands={rowsOf(DOC_SCOPES.document)}
						opens={registry
							.keysOf("sheet")
							.map(printKeys)
							.join(` ${t("shell.shortcuts.or")} `)}
						mod={MOD_KEY}
						mac={IS_MAC}
						Chords={SheetChords}
					/>
				</Suspense>
				{moduleScopes.map(({ scope, titleKey }) => (
					<Section key={scope} titleKey={titleKey} rows={rowsOf(scope)} />
				))}
			</div>
		</Dialog>
	)
}
